import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, onBeforeMount } from "vue";
import { IncomingBillService } from "@/services/IncomingBillService";
import { useToast } from "@/services/useToast";
import { formatPublicReference } from "@/utils/formatReference";
import router from "@/router";
import ResourceErrorState from "@/components/layout/ResourceErrorState.vue";
import { useResourceState } from "@/services/useResourceState";


export default /*#__PURE__*/_defineComponent({
  __name: 'AdminBillDetailsView',
  setup(__props, { expose: __expose }) {
  __expose();

const billService = new IncomingBillService();
const { showToast } = useToast();
const billId = Number(router.currentRoute.value.params.id);

const bill = ref<any>({});
const newStatus = ref("");
const saving = ref(false);
const itemsSaving = ref(false);
const attachedItems = ref<any[]>([]);
const searchResults = ref<any[]>([]);
const searched = ref(false);
const filters = ref({ accommodation: "", startDate: "", endDate: "" });

const newPaidAt = ref<string>("");
const originalPaidAt = ref<string>("");
const newAmount = ref<string>("");
const originalAmount = ref<string>("");
const originalItemIds = ref<number[]>([]);

const { loading, error, runLoad } = useResourceState();

/** Fills the form from the bill; also the retry path, so it must be idempotent. */
const seedFromBill = () => {
  newStatus.value = bill.value.status;
  const paidAtDateString = bill.value.paidAt
    ? new Date(bill.value.paidAt).toISOString().substring(0, 10)
    : "";
  newPaidAt.value = paidAtDateString;
  originalPaidAt.value = paidAtDateString;
  const amountString =
    bill.value.amount != null ? String(bill.value.amount) : "";
  newAmount.value = amountString;
  originalAmount.value = amountString;
  attachedItems.value = [...(bill.value.bookingItems ?? [])];
  originalItemIds.value = attachedItems.value.map((i: any) => i.id);
};

const fetchData = () =>
  runLoad(async () => {
    bill.value = await billService.get(billId);
    seedFromBill();
  });

onBeforeMount(fetchData);

const itemsChanged = computed(() => {
  const current = attachedItems.value
    .map((i) => i.id)
    .sort()
    .join(",");
  const original = [...originalItemIds.value].sort().join(",");
  return current !== original;
});

const canSearch = computed(
  () =>
    !!filters.value.accommodation ||
    !!filters.value.startDate ||
    !!filters.value.endDate
);

const isAttached = (id: number) => attachedItems.value.some((i) => i.id === id);

const addItem = (item: any) => {
  if (!isAttached(item.id)) attachedItems.value.push(item);
};

const removeItem = (item: any) => {
  attachedItems.value = attachedItems.value.filter((i) => i.id !== item.id);
};

const searchBookingItems = async () => {
  const params: Record<string, string> = {};
  if (filters.value.accommodation)
    params.accommodation = filters.value.accommodation;
  if (filters.value.startDate) params.startDate = filters.value.startDate;
  if (filters.value.endDate) params.endDate = filters.value.endDate;
  searchResults.value = (await billService.getBookingItems(params)) ?? [];
  searched.value = true;
};

const saveBookingItems = async () => {
  if (itemsSaving.value) return;
  itemsSaving.value = true;
  try {
    const ids = attachedItems.value.map((i) => i.id);
    bill.value = await billService.updateBookingItems(billId, ids);
    attachedItems.value = [...(bill.value.bookingItems ?? [])];
    originalItemIds.value = attachedItems.value.map((i) => i.id);
    showToast("Foglalás elemek mentve");
  } catch {
    showToast("Mentés sikertelen", "error");
  } finally {
    itemsSaving.value = false;
  }
};

const statusOptions = [
  { label: "Esedékes", value: "DUE" },
  { label: "Fizetve", value: "PAID" },
  { label: "Törölve", value: "CANCELLED" },
  { label: "Lejárt", value: "OVERDUE" },
];

const STATUS_LABELS: Record<string, string> = {
  DUE: "Esedékes",
  PAID: "Fizetve",
  CANCELLED: "Törölve",
  OVERDUE: "Lejárt",
};

const STATUS_COLORS: Record<string, string> = {
  DUE: "orange",
  PAID: "green",
  CANCELLED: "grey",
  OVERDUE: "red",
};

const TYPE_LABELS: Record<string, string> = {
  ACCOMMODATION: "Szállás",
  IFA: "IFA",
};

const TYPE_COLORS: Record<string, string> = {
  ACCOMMODATION: "blue",
  IFA: "purple",
};

const statusLabel = (s: string) => STATUS_LABELS[s] ?? s;
const statusColor = (s: string) => STATUS_COLORS[s] ?? "grey";

const typeLabel = (t: string) => TYPE_LABELS[t] ?? t;
const typeColor = (t: string) => TYPE_COLORS[t] ?? "grey";

const formatDate = (ts: number) => {
  if (!ts) return "-";
  return new Date(ts).toLocaleDateString("hu-HU", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatAmount = (value: number | null | undefined) =>
  value == null ? "-" : `${value.toLocaleString("hu-HU")} Ft`;

const formatDateOnly = (ts: number) => {
  if (!ts) return "-";
  return new Date(ts).toLocaleDateString("hu-HU", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
};

const parsedAmount = computed(() => {
  const value = Number(newAmount.value);
  return newAmount.value !== "" && Number.isFinite(value) ? value : null;
});

const hasChanges = computed(
  () =>
    newStatus.value !== bill.value.status ||
    newPaidAt.value !== originalPaidAt.value ||
    (newAmount.value !== originalAmount.value && parsedAmount.value !== null)
);

const saveChanges = async () => {
  if (saving.value) return;
  saving.value = true;
  try {
    const payload: {
      status?: string;
      amount?: number;
      paidAt?: number | null;
    } = {};
    if (newStatus.value !== bill.value.status) payload.status = newStatus.value;
    if (
      newAmount.value !== originalAmount.value &&
      parsedAmount.value !== null
    ) {
      payload.amount = parsedAmount.value;
    }
    if (newPaidAt.value !== originalPaidAt.value) {
      payload.paidAt = newPaidAt.value
        ? new Date(newPaidAt.value).getTime()
        : null;
    }
    bill.value = await billService.patch(billId, payload);
    originalPaidAt.value = newPaidAt.value;
    originalAmount.value = newAmount.value;
    showToast("Mentve");
  } catch {
    showToast("Mentés sikertelen", "error");
  } finally {
    saving.value = false;
  }
};

const commonItemColumns = [
  { title: "Szálláshely", key: "accommodation" },
  { title: "Termék", key: "product" },
  { title: "Cég", key: "companyName" },
  { title: "Érkezés", key: "startDate" },
  { title: "Távozás", key: "endDate" },
  { title: "Létszám", key: "numberOfPeople", align: "end" as const },
  {
    title: "Bekerülési egységár",
    key: "grossPricePerPersonPerNight",
    align: "end" as const,
  },
  { title: "Bekerülési összár", key: "totalGrossPrice", align: "end" as const },
  { title: "Foglalás ref.", key: "bookingPublicReference" },
];

const attachedHeaders = [
  ...commonItemColumns,
  { title: "", key: "actions", sortable: false, align: "end" as const },
];

const searchHeaders = [
  ...commonItemColumns,
  { title: "", key: "actions", sortable: false, align: "end" as const },
];

const __returned__ = { billService, showToast, billId, bill, newStatus, saving, itemsSaving, attachedItems, searchResults, searched, filters, newPaidAt, originalPaidAt, newAmount, originalAmount, originalItemIds, loading, error, runLoad, seedFromBill, fetchData, itemsChanged, canSearch, isAttached, addItem, removeItem, searchBookingItems, saveBookingItems, statusOptions, STATUS_LABELS, STATUS_COLORS, TYPE_LABELS, TYPE_COLORS, statusLabel, statusColor, typeLabel, typeColor, formatDate, formatAmount, formatDateOnly, parsedAmount, hasChanges, saveChanges, commonItemColumns, attachedHeaders, searchHeaders, ref, computed, onBeforeMount, get IncomingBillService() { return IncomingBillService }, get useToast() { return useToast }, get formatPublicReference() { return formatPublicReference }, get router() { return router }, ResourceErrorState, get useResourceState() { return useResourceState } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})